/**
 * Template `inbox_antwoord`: een kantoorantwoord op een WhatsApp-bericht van
 * de klant nadat het 24-uursvenster dicht is. Meta laat dan geen vrije tekst
 * meer toe; een utility-template met een vaste kop en het antwoord als
 * parameter wel (plan 2026-11-03-whatsapp-buiten-servicevenster, laag 2).
 *
 * Alles hier is zuiver (geen I/O): de send-service en de composer gebruiken
 * dezelfde functies, zodat de thread precies toont wat de klant zag en de
 * grens op één plek staat.
 *
 * Meta-parameterregels (de reden voor sanering en grens):
 *  - geen newline of tab in een parameter, niet meer dan vier spaties op rij;
 *  - de hele body maximaal 1024 tekens;
 *  - een template mag niet alleen uit een variabele bestaan (daarom de kop).
 */

/**
 * Exacte body per WABA: Kleda goedgekeurd 03-11 (id 4400893940174577), Cinco
 * ingediend 03-11 met "over je aanvraag" (Cinco is een platform; het gaat niet
 * per se om een vloerkleed). Dezelfde slotzin.
 */
export const INBOX_ANTWOORD_TEMPLATE = 'inbox_antwoord';
export const INBOX_ANTWOORD_KOP = 'Hoi {{1}}, je stuurde ons een WhatsApp over je vloerkleed. Ons antwoord: {{2}}';
export const INBOX_ANTWOORD_KOP_CINCO = 'Hoi {{1}}, je stuurde ons een WhatsApp over je aanvraag. Ons antwoord: {{2}}';
export const INBOX_ANTWOORD_SLOT = 'Heb je nog een vraag? Reageer dan gewoon op dit bericht.';

/** De kop die op de WABA van dit merk is ingediend. */
export function inboxAntwoordKop(brand: string | null | undefined): string {
  return (brand ?? '').toUpperCase() === 'CINCO' ? INBOX_ANTWOORD_KOP_CINCO : INBOX_ANTWOORD_KOP;
}
/** Idempotentie-context in wa_send_log: één template-antwoord per antwoordslot. */
export const INBOX_ANTWOORD_CONTEXT_TYPE = 'inbox-antwoord';
/** Meta-body 1024 minus de vaste kop en slotzin (128 tekens) en een voornaam van maximaal 30. */
export const INBOX_ANTWOORD_MAX_TEKENS = 860;
export const INBOX_ANTWOORD_MAX_VOORNAAM = 30;
/** Zonder naam in dossier, offerte of profiel: nooit een lege parameter sturen. */
export const INBOX_ANTWOORD_NAAM_TERUGVAL = 'beste klant';

/**
 * Maakt van vrije kantoortekst één parameter die Meta accepteert: regels en
 * tabs worden spaties, dubbele spaties één, randen getrimd. De inhoud blijft
 * de inhoud; alleen de opmaak die een template niet kan dragen verdwijnt.
 */
export function saneerTemplateParameter(tekst: string): string {
  return (tekst ?? '')
    .replace(/[\r\n\t\v\f\u0085\u2028\u2029]+/g, ' ')
    .replace(/ {2,}/g, ' ')
    .trim();
}

/** Voornaam voor parameter 1: eerste woord van een naam, zonder aanhef of titel. */
export function voornaamVoorTemplate(naam: string | null | undefined): string {
  const schoon = saneerTemplateParameter(naam ?? '')
    .replace(/^(dhr|mevr|mw|mr|mrs|ms|de heer|mevrouw|fam|familie|dr|ing|drs)\.?\s+/i, '')
    .trim();
  const eerste = schoon.split(' ')[0] ?? '';
  // Een initiaal ("J." of "J") of een leeg woord is geen aanspreeknaam.
  if (!eerste || /^[A-Za-z]\.?$/.test(eerste)) return INBOX_ANTWOORD_NAAM_TERUGVAL;
  return eerste.slice(0, INBOX_ANTWOORD_MAX_VOORNAAM);
}

/**
 * Kies uit meerdere naambronnen (dossier, offerte, WhatsApp-profiel) de eerste
 * die een echte voornaam oplevert. Les 03-11 (Van Imhoff): het dossier heette
 * "B Van Imhoff", de initiaal viel terug op "beste klant" terwijl het profiel
 * gewoon "Barbara" zei. Een bron die alleen de terugval oplevert telt niet.
 */
export function eersteNaamMetVoornaam(...kandidaten: Array<string | null | undefined>): string | null {
  for (const k of kandidaten) {
    if (k && voornaamVoorTemplate(k) !== INBOX_ANTWOORD_NAAM_TERUGVAL) return k;
  }
  return null;
}

export type InboxAntwoordParameters =
  | { ok: true; voornaam: string; antwoord: string }
  | { ok: false; code: 'lege_tekst' | 'antwoord_te_lang_voor_template'; uitleg: string };

/** Beide parameters gesaneerd en getoetst; de grens geldt na sanering. */
export function bereidInboxAntwoordParameters(input: { voornaam: string | null | undefined; antwoord: string }): InboxAntwoordParameters {
  const antwoord = saneerTemplateParameter(input.antwoord);
  if (!antwoord) return { ok: false, code: 'lege_tekst', uitleg: 'Een WhatsApp-antwoord mag niet leeg zijn.' };
  if (antwoord.length > INBOX_ANTWOORD_MAX_TEKENS) {
    return {
      ok: false,
      code: 'antwoord_te_lang_voor_template',
      uitleg: `Het antwoord is ${antwoord.length} tekens; de template draagt maximaal ${INBOX_ANTWOORD_MAX_TEKENS}. Kort het in.`,
    };
  }
  return { ok: true, voornaam: voornaamVoorTemplate(input.voornaam), antwoord };
}

/** Meta components-array voor sendWhatsAppTemplate: body met twee tekstparameters. */
export function bouwInboxAntwoordComponents(voornaam: string, antwoord: string): unknown[] {
  return [{
    type: 'body',
    parameters: [
      { type: 'text', text: voornaam },
      { type: 'text', text: antwoord },
    ],
  }];
}

/** Exact de tekst die de klant ziet; gaat als lokaal uitgaand bericht op de thread. */
export function renderInboxAntwoord(voornaam: string, antwoord: string, brand: string | null | undefined = 'KLEDA'): string {
  return `${inboxAntwoordKop(brand).replace('{{1}}', voornaam).replace('{{2}}', antwoord)}\n\n${INBOX_ANTWOORD_SLOT}`;
}

/** Herkent een gerenderd template-antwoord op de thread (voor het label "template" in de composer), voor beide merkkoppen. */
export function isGerenderdInboxAntwoord(bodyText: string | null | undefined): boolean {
  if (!bodyText) return false;
  return [INBOX_ANTWOORD_KOP, INBOX_ANTWOORD_KOP_CINCO].some((kop) => {
    const kopPrefix = kop.split('{{1}}')[0] ?? '';
    const kopMidden = kop.split('{{1}}')[1]?.split('{{2}}')[0] ?? '';
    return bodyText.startsWith(kopPrefix) && bodyText.includes(kopMidden) && bodyText.trimEnd().endsWith(INBOX_ANTWOORD_SLOT);
  });
}
